import * as React from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';

import AppBar from '@material-ui/core/AppBar';
import IconButton from '@material-ui/core/IconButton';
import SvgIcon from '@material-ui/core/SvgIcon';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/lab/Slider';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';

import SlideAddIcon from '@material-ui/icons/AddToQueueRounded';
import SelectSketchRectIcon from '@material-ui/icons/CheckBoxOutlineBlankRounded';
import SelectSketchEllipse from '@material-ui/icons/RadioButtonUncheckedRounded';
import SelectSketchFreehandIcon from '@material-ui/icons/GestureRounded';
import HelpIcon from '@material-ui/icons/HelpRounded';
import SelectThreeMainSectionsIcon from '@material-ui/icons/Looks3Rounded';
import SelectFourMainSectionsIcon from '@material-ui/icons/Looks4Rounded';
import SelectFiveMainSectionsIcon from '@material-ui/icons/Looks5Rounded';
import NoteAddIcon from '@material-ui/icons/NoteAddRounded';
import RedoIcon from '@material-ui/icons/RedoRounded';
import SelectSketchLineIcon from '@material-ui/icons/RemoveRounded';
import SelectSketchTextIcon from '@material-ui/icons/TextFieldsRounded';
import SelectSketchArrowIcon from '@material-ui/icons/TrendingFlatRounded';
import UndoIcon from '@material-ui/icons/UndoRounded';
import ZoomInIcon from '@material-ui/icons/ZoomInRounded';
import ZoomOutIcon from '@material-ui/icons/ZoomOutRounded';

import { GREEN, NoteColor, ORANGE, PINK, ProcessStep, YELLOW } from '../constants';
import { SketchMode } from '../reducers';


interface Props {
    readonly isDrawerVisible: boolean,
    readonly isUndoable: boolean,
    readonly isRedoable: boolean,
    readonly processStep: ProcessStep,
    readonly templateId: number,
    readonly sketchMode: SketchMode,
    readonly zoomSliderValue: number,
    readonly onRedo: () => void,
    readonly onUndo: () => void,
    readonly onSelectTemplate: (templateId: number) => void,
    readonly onSelectSketchMode: (sketchMode: SketchMode) => void,
    readonly onShowCreateNoteDialog: (color: NoteColor) => void,
    readonly onCreateSlide: () => void,
    readonly onZoomOutClicked: () => void,
    readonly onZoomInClicked: () => void,
    readonly onZoomLevelChanged: (event: React.ChangeEvent<{}>, zoomLevel: number) => void,
    readonly onShowHelp: () => void,
}


const styles = (theme: Theme) => createStyles({
    toolbar: {
        padding: 0,
        borderBottom: `solid thin ${theme.palette.divider}`,
    },
    category: {
        alignItems: 'center',
        borderRight: `solid thin ${theme.palette.divider}`,
        paddingLeft: theme.spacing.unit,
        paddingRight: theme.spacing.unit,
        whiteSpace: 'nowrap',
    },
    divider: {
        flex: 1,
    },
    selectedButton: {
        color: theme.palette.primary.main + ' !important',
    },
    sliderRoot: {
        flex: 1,
        height: 48,
        marginLeft: 8,
        marginRight: 4,
    },
    sliderContent: {
        marginTop: 24,
    },
    sliderArea: {
        borderLeft: `solid thin ${theme.palette.divider}`,
        borderRight: `solid thin ${theme.palette.divider}`,
        flexGrow: 2,
        maxWidth: '25%',
        paddingLeft: theme.spacing.unit,
        paddingRight: 2 * theme.spacing.unit,
    },
    sliderBox: {
        alignSelf: 'stretch'
    },
    sliderLeftLabel: {
        // marginTop: '13px !important',
        // marginRight: 8
    },
    sliderRightLabel: {
        // marginTop: '13px !important',
        // marginLeft: 4
    },
    helpButton: {
        marginLeft: 12,
        marginRight: 20,
        color: theme.palette.primary.main,
    },
    hide: {
        display: 'none',
    }
});

type Styles =
    'toolbar' | 'category' | 'divider'
    | 'selectedButton'
    | 'sliderArea' | 'sliderBox' | 'sliderRoot' | 'sliderContent' | 'sliderLeftLabel' | 'sliderRightLabel'
    | 'helpButton' | 'hide';


const MainToolbar = ({
                         isDrawerVisible,
                         isUndoable,
                         isRedoable,
                         processStep,
                         templateId,
                         sketchMode,
                         zoomSliderValue,
                         classes,
                         intl,
                         onRedo,
                         onUndo,
                         onSelectTemplate,
                         onSelectSketchMode,
                         onShowCreateNoteDialog,
                         onCreateSlide,
                         onShowHelp,
                         onZoomInClicked,
                         onZoomOutClicked,
                         onZoomLevelChanged,
                     }: Props & WithStyles<Styles> & InjectedIntlProps) => {
    return (
        <AppBar color="inherit" position="static" elevation={0}>
            <Toolbar className={classes.toolbar}>
                <div className={`vbox ${classes.category}`}>
                    <Typography variant="caption" align="center">
                        <FormattedMessage id="undo_label"/>
                    </Typography>
                    <div className="hbox">
                        <Tooltip title={intl.formatMessage({id: 'undo'})}>
                            <div>
                                <IconButton onClick={() => onUndo()} disabled={!isUndoable}>
                                    <UndoIcon/>
                                </IconButton>
                            </div>
                        </Tooltip>
                        <Tooltip title={intl.formatMessage({id: 'redo'})}>
                            <div>
                                <IconButton onClick={() => onRedo()} disabled={!isRedoable}>
                                    <RedoIcon/>
                                </IconButton>
                            </div>
                        </Tooltip>
                    </div>
                </div>
                <div className={`vbox ${classes.category}`}>
                    <Typography variant="caption" align="center">
                        <FormattedMessage id="add_note_label"/>
                    </Typography>
                    {processStep === ProcessStep.BRAINSTORM || processStep === ProcessStep.STRUCTURE || processStep === ProcessStep.SKETCH ?
                        <div className="hbox">
                            <Tooltip title={intl.formatMessage({id: 'add_yellow_note_tooltip'})}>
                                <IconButton onClick={() => onShowCreateNoteDialog(NoteColor.YELLOW)}>
                                    <NoteAddIcon style={{fill: YELLOW}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={intl.formatMessage({id: 'add_pink_note_tooltip'})}>
                                <IconButton onClick={() => onShowCreateNoteDialog(NoteColor.PINK)}>
                                    <NoteAddIcon style={{fill: PINK}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={intl.formatMessage({id: 'add_green_note_tooltip'})}>
                                <IconButton onClick={() => onShowCreateNoteDialog(NoteColor.GREEN)}>
                                    <NoteAddIcon style={{fill: GREEN}}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={intl.formatMessage({id: 'add_orange_note_tooltip'})}>
                                <IconButton onClick={() => onShowCreateNoteDialog(NoteColor.ORANGE)}>
                                    <NoteAddIcon style={{fill: ORANGE}}/>
                                </IconButton>
                            </Tooltip>
                        </div>
                        :
                        <div className="hbox">
                            <IconButton disabled={true}>
                                <NoteAddIcon/>
                            </IconButton>
                            <IconButton disabled={true}>
                                <NoteAddIcon/>
                            </IconButton>
                            <IconButton disabled={true}>
                                <NoteAddIcon/>
                            </IconButton>
                            <IconButton disabled={true}>
                                <NoteAddIcon/>
                            </IconButton>
                        </div>
                    }
                </div>
                {
                    processStep === ProcessStep.STRUCTURE &&
                    <div className={`vbox ${classes.category}`}>
                        <Typography variant="caption" align="center">
                            <FormattedMessage id="number_of_main_sections"/>
                        </Typography>
                        <div className="hbox">
                            {templateId === 1 ?
                                <IconButton disabled={true} className={classes.selectedButton}>
                                    <SelectThreeMainSectionsIcon/>
                                </IconButton>
                                :
                                <Tooltip title={intl.formatMessage({id: 'select_three_main_sections_tooltip'})}>
                                    <IconButton onClick={() => onSelectTemplate(1)}>
                                        <SelectThreeMainSectionsIcon/>
                                    </IconButton>
                                </Tooltip>
                            }
                            {templateId === 2 ?
                                <IconButton disabled={true} className={classes.selectedButton}>
                                    <SelectFourMainSectionsIcon/>
                                </IconButton>
                                :
                                <Tooltip title={intl.formatMessage({id: 'select_four_main_sections_tooltip'})}>
                                    <IconButton onClick={() => onSelectTemplate(2)}>
                                        <SelectFourMainSectionsIcon/>
                                    </IconButton>
                                </Tooltip>
                            }
                            {templateId === 3 ?
                                <IconButton disabled={true} className={classes.selectedButton}>
                                    <SelectFiveMainSectionsIcon/>
                                </IconButton>
                                :
                                <Tooltip title={intl.formatMessage({id: 'select_five_main_sections_tooltip'})}>
                                    <IconButton onClick={() => onSelectTemplate(3)}>
                                        <SelectFiveMainSectionsIcon/>
                                    </IconButton>
                                </Tooltip>
                            }
                        </div>
                    </div>
                }
                {
                    processStep === ProcessStep.SKETCH &&
                    <div className="hbox">
                        <div className={`vbox ${classes.category}`}>
                            <Typography variant="caption" align="center">
                                <FormattedMessage id="add_slide_tooltip"/>
                            </Typography>
                            <Tooltip title={intl.formatMessage({id: 'add_slide_tooltip'})}>
                                <IconButton onClick={() => onCreateSlide()}>
                                    <SlideAddIcon/>
                                </IconButton>
                            </Tooltip>
                        </div>
                        <div className={`vbox ${classes.category}`}>
                            <Typography variant="caption" align="center">
                                <FormattedMessage id="select_sketch_label"/>
                            </Typography>
                            <div className="hbox">
                                {sketchMode === 'NONE' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SvgIcon>
                                            <path
                                                d="M8.3,3.213l9.468,8.836c0.475,0.443,0.2,1.24-0.447,1.296L13.2,13.7l2.807,6.21c0.272,0.602,0.006,1.311-0.596,1.585l0,0 c-0.61,0.277-1.33,0-1.596-0.615L11.1,14.6l-2.833,2.695C7.789,17.749,7,17.411,7,16.751V3.778C7,3.102,7.806,2.752,8.3,3.213z"/>
                                        </SvgIcon>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_none_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("NONE")}>
                                            <SvgIcon>
                                                <path
                                                    d="M8.3,3.213l9.468,8.836c0.475,0.443,0.2,1.24-0.447,1.296L13.2,13.7l2.807,6.21c0.272,0.602,0.006,1.311-0.596,1.585l0,0 c-0.61,0.277-1.33,0-1.596-0.615L11.1,14.6l-2.833,2.695C7.789,17.749,7,17.411,7,16.751V3.778C7,3.102,7.806,2.752,8.3,3.213z"/>
                                            </SvgIcon>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {sketchMode === 'LINE' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SelectSketchLineIcon/>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_line_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("LINE")}>
                                            <SelectSketchLineIcon/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {sketchMode === 'ARROW' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SelectSketchArrowIcon/>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_arrow_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("ARROW")}>
                                            <SelectSketchArrowIcon/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {sketchMode === 'RECT' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SelectSketchRectIcon/>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_rect_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("RECT")}>
                                            <SelectSketchRectIcon/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {sketchMode === 'ELLIPSE' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SelectSketchEllipse/>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_ellipse_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("ELLIPSE")}>
                                            <SelectSketchEllipse/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {sketchMode === 'FREEFORM' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SelectSketchFreehandIcon/>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_freehand_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("FREEFORM")}>
                                            <SelectSketchFreehandIcon/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {sketchMode === 'TEXT' ?
                                    <IconButton disabled={true} className={classes.selectedButton}>
                                        <SelectSketchTextIcon/>
                                    </IconButton>
                                    :
                                    <Tooltip title={intl.formatMessage({id: 'select_sketch_text_tooltip'})}>
                                        <IconButton onClick={() => onSelectSketchMode("TEXT")}>
                                            <SelectSketchTextIcon/>
                                        </IconButton>
                                    </Tooltip>
                                }
                            </div>
                        </div>
                    </div>
                }
                <div className={classes.divider}/>
                <div className={`vbox ${classes.sliderArea} ${classes.category}`}>
                    <Typography variant="caption" align="center">
                        Zoom
                    </Typography>
                    {processStep === ProcessStep.BRAINSTORM || processStep === ProcessStep.STRUCTURE || processStep === ProcessStep.SKETCH ?
                        <div className={`hbox ${classes.sliderBox}`}>
                            <Tooltip title={intl.formatMessage({id: 'zoom_out_tooltip'})}>
                                <IconButton onClick={onZoomOutClicked}>
                                    <ZoomOutIcon className={classes.sliderLeftLabel}/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={intl.formatMessage({id: 'zoom_tooltip'})}>
                                <Slider
                                    classes={{
                                        root: classes.sliderRoot,
                                        thumb: classes.sliderContent,
                                        track: classes.sliderContent,
                                    }}
                                    value={zoomSliderValue}
                                    min={0.0}
                                    max={1.0}
                                    step={0.1}
                                    onChange={onZoomLevelChanged}
                                />
                            </Tooltip>
                            <Tooltip title={intl.formatMessage({id: 'zoom_in_tooltip'})}>
                                <IconButton onClick={onZoomInClicked}>
                                    <ZoomInIcon className={classes.sliderRightLabel}/>
                                </IconButton>
                            </Tooltip>
                        </div>
                        :
                        <div className={`hbox ${classes.sliderBox}`}>
                            <IconButton disabled={true}>
                                <ZoomOutIcon className={classes.sliderLeftLabel}/>
                            </IconButton>
                            <Slider
                                classes={{
                                    root: classes.sliderRoot,
                                    thumb: classes.sliderContent,
                                    track: classes.sliderContent,
                                }}
                                disabled={true}
                                value={zoomSliderValue}
                                min={0.0}
                                max={1.0}
                                step={0.1}
                            />
                            <IconButton disabled={true}>
                                <ZoomInIcon className={classes.sliderRightLabel}/>
                            </IconButton>
                        </div>
                    }
                </div>
                <div className={classes.divider}/>
                {isDrawerVisible ?
                    <IconButton disabled={true} className={classes.helpButton}>
                        <HelpIcon/>
                    </IconButton>
                    :
                    <Tooltip title={intl.formatMessage({id: 'show_help'})}>
                        <IconButton onClick={onShowHelp} className={classes.helpButton}>
                            <HelpIcon/>
                        </IconButton>
                    </Tooltip>
                }
            </Toolbar>
        </AppBar>
    );
};

export default withStyles(styles)(injectIntl(MainToolbar));



// WEBPACK FOOTER //
// ./src/components/MainToolbarView.tsx